export interface XPage<T> {
  data: T[];
  offset: number;
  size: number;
  /** Offset for the next page, or null when there are no more pages (matches fama_interfaces.common.XPage). */
  next: number | null;
}

/** Next page offset from the server, or null when pagination is complete. */
export function xpageNextOffset(
  page: XPage<unknown>,
  currentOffset: number = page.offset
): number | null {
  const next = page.next;
  // Standard XPage uses null on the last page; legacy responses used 0 or
  // repeated the current offset to mean "stop".
  if (next == null || next <= currentOffset) {
    return null;
  }
  return next;
}

/** Whether the server indicated another page exists (matches famautils.xpaginate semantics). */
export function xpageHasNext(page: XPage<unknown>): boolean {
  return xpageNextOffset(page) != null;
}

/**
 * Best-effort total row count for paginated UI with zero-based row offsets.
 * When `next` is null (final page), total is offset + current page length.
 * Otherwise uses the larger of `next` and loaded rows so legacy responses
 * that store total count in `next` still work.
 *
 * For APIs that put a 1-based page number in `offset` and total count in `next`
 * (e.g. recur list), use {@link xpagePageNumberTotalCount} instead.
 */
export function xpageTotalCount(page: XPage<unknown>): number {
  const loadedThrough = page.offset + page.data.length;
  if (page.next === null) {
    return loadedThrough;
  }
  return Math.max(page.next, loadedThrough);
}

/**
 * Total row count when `offset` is a 1-based page number and `next` is the
 * total row count (recur list XPage semantics).
 */
export function xpagePageNumberTotalCount(
  page: XPage<unknown>,
  pageSize: number
): number {
  if (page.next != null) {
    return page.next;
  }
  return (page.offset - 1) * pageSize + page.data.length;
}

export interface BasicInfo {
  uuid: string;
  name: string;
}
